import "../../css/FullEvent/SelectCircleRow.css";
import "../../css/GlobalStyles.css";
import React, { memo, useMemo, useState } from "react";
import { isString } from "@markit/common.utils";
import { Colors } from "../../utils/colors";
import { useTheme } from "../../hooks/useTheme";
import { CustomTicketV2, Event } from "@markit/common.types";
import { atCapacityEventLabel } from "../../utils/eventUtils/eventUtils";
import MediaExpandedItem from "../Media/MediaExpandedItem";

type SelectCircleRowProps = {
  event: Event;
  customTicket: CustomTicketV2;
  onPress: () => void;
  description: string;
  priceTag: JSX.Element | undefined;
  multiTicketText: React.ReactNode | null;
  isSelected: boolean;
  isCheckout: boolean; // Only the right icon is selectable in checkout
  isDisabled: boolean;
  isSoldOut: boolean;
  rightIcon: JSX.Element;
  outerRightIcon?: JSX.Element; // useful for top level ticket options
};

export const SelectCircleRow = memo(function SelectCircleRowFn(
  props: SelectCircleRowProps
) {
  const {
    event,
    customTicket,
    description,
    priceTag,
    multiTicketText,
    isSelected,
    isCheckout,
    isDisabled,
    isSoldOut,
    onPress,
    rightIcon,
    outerRightIcon,
  } = props;

  const { theme } = useTheme();
  const [expandedImageUrl, setExpandedImageUrl] = useState("");

  // Whether there are additional details to show on the ticket option
  const hasDetails = useMemo(
    () => !!customTicket.description || !!multiTicketText || !!description,
    [customTicket.description, multiTicketText, description]
  );

  const hasDetailsOrImage = useMemo(
    () => hasDetails || customTicket.mediaURL,
    [hasDetails, customTicket.mediaURL]
  );

  // Helps decide whether to add additional padding to the price tag section, if the flexible input is showing
  const isFlexiblePricingSelected = useMemo(
    () => customTicket.flexiblePricing && isSelected && isCheckout,
    [customTicket.flexiblePricing, isSelected, isCheckout]
  );

  return (
    <>
      <div
        className="SelectContainer"
        onClick={isDisabled ? undefined : onPress}
        style={{
          borderColor:
            isSelected && !isDisabled
              ? theme.FocusedTextFieldBorder.borderColor
              : theme.TextFieldBorder.borderColor,
          borderWidth: 1.5,
          backgroundColor: theme.SecondaryBG.backgroundColor,
          pointerEvents: isDisabled ? "none" : "all",
          cursor: isCheckout ? "auto" : "pointer",
        }}
      >
        <div className="SelectDetails">
          <div className="ColumnNormal" style={{ width: "100%", gap: 7 }}>
            <div
              className="AlignedRowSpaced"
              style={{ gap: "15px", opacity: isDisabled ? 0.3 : 1 }}
            >
              <div style={{ ...theme.PrimaryText }}>
                {isString(customTicket.label) ? (
                  <p className="TicketText">{customTicket.label}</p>
                ) : (
                  customTicket.label
                )}
              </div>
              {isSoldOut ? (
                <div
                  className="AlignedRow"
                  style={{
                    backgroundColor: Colors.GRAY1,
                    padding: "2px 8px",
                    borderRadius: 12,
                  }}
                >
                  <span
                    style={{
                      ...theme.PrimaryText,
                      fontSize: 14,
                      fontWeight: "600",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {atCapacityEventLabel(event, customTicket)}
                  </span>
                </div>
              ) : (
                <div className="AlignedRow">
                  {!isDisabled ? rightIcon : null}
                </div>
              )}
            </div>
            {priceTag ? (
              <span
                style={{
                  ...theme.PrimaryText,
                  opacity: isDisabled ? 0.3 : 1,
                  paddingTop: isFlexiblePricingSelected ? 10 : 0,
                  paddingBottom:
                    isFlexiblePricingSelected && hasDetailsOrImage ? 10 : 0,
                }}
              >
                {priceTag}
              </span>
            ) : null}
            {hasDetailsOrImage ? (
              <div className="ColumnNormal" style={{ gap: 10 }}>
                {hasDetails ? (
                  <div className="ColumnNormal" style={{ gap: 4 }}>
                    {customTicket.description ? (
                      <span
                        className="smallBodySubtext"
                        style={{
                          ...theme.SubText,
                          opacity: isDisabled ? 0.3 : 1,
                        }}
                      >
                        {customTicket.description}
                      </span>
                    ) : null}
                    {multiTicketText || null}
                    {description ? (
                      <span
                        className="smallBodySubtext"
                        style={{
                          color: Colors.ORANGE1,
                          opacity: isDisabled ? 0.3 : 1,
                        }}
                      >
                        {description.substring(
                          description.indexOf(".") + 1,
                          description.length
                        )}
                      </span>
                    ) : null}
                  </div>
                ) : null}
                {customTicket.mediaURL ? (
                  <div
                    style={{
                      cursor: "pointer",
                      width: "fit-content",
                      lineHeight: 0,
                      opacity: isDisabled ? 0.3 : 1,
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setExpandedImageUrl(customTicket.mediaURL);
                    }}
                  >
                    <img
                      src={customTicket.mediaURL}
                      alt="Ticket Image"
                      style={{
                        width: 70,
                        borderRadius: 8,
                        border: `0.5px solid ${theme.EditableTextFieldBorder.borderColor}`,
                      }}
                    />
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
          {outerRightIcon}
        </div>
      </div>
      {expandedImageUrl ? (
        <MediaExpandedItem
          mediaUrl={expandedImageUrl}
          onClose={() => setExpandedImageUrl("")}
          hardTheme={event.theme}
        />
      ) : null}
    </>
  );
});
